"use client";

import {
  Box,
  Container,
  Text,
  Button,
  SimpleGrid,
  Stack,
  Modal,
  Flex,
  rem,
  Alert,
  Paper,
  Group,
} from "@mantine/core";
import { Dropzone } from "@mantine/dropzone";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import {
  IconCloudUpload,
  IconX,
  IconCamera,
  IconDownload,
  IconArrowRight,
} from "@tabler/icons-react";
import React, { useEffect, useRef, useState, ReactNode } from "react";
import { useRouter } from "@/i18n/navigation";
import { QUICK_ANALYSIS_V1 } from "@/lib/quickAnalysisFlag";
import { PRICING } from "@/constants/pricing";
import { trackCtaClick, trackPhotoUpload } from "@/lib/analytics";
import { useTranslations } from "next-intl";
import classes from "./landing.module.css";
import SampleReportButton from "@/components/SampleReportButton";

/* Atelier system devices (match home.module.css) */
const SEASON_DOTS = ["#FF6F61", "#5D6D8E", "#B87333", "#0055FF"];
const RAIL_STOPS: [string, string, string][] = [
  ["#FFDAB3", "#FBCEB1", "#F88379"],
  ["#FFBF00", "#FF6F61", "#FF6347"],
  ["#FFF700", "#FF9900", "#FF5E5B"],
  ["#FFD1DC", "#B4CFEC", "#C8A2C8"],
  ["#D4A5A5", "#E0B0FF", "#5D6D8E"],
  ["#C76B8A", "#3E8E8E", "#3B5998"],
  ["#D4AF37", "#C87C5A", "#808000"],
  ["#F4C430", "#FF7518", "#B87333"],
  ["#B87333", "#8A3324", "#4A1C17"],
  ["#A5F2F3", "#007FFF", "#990066"],
  ["#E5F9F9", "#0055FF", "#000000"],
  ["#005F5F", "#000080", "#4A0E0E"],
];

/* Real reviews — quotes are verbatim customer reviews (Etsy shop + email).
   Never swap in invented quotes or stock avatar photos here. */
const REVIEWS = [
  { quoteKey: "review1Quote", name: "KB", season: "Deep Autumn" },
  { quoteKey: "review2Quote", name: "Derek L.", season: "Warm Spring" },
  { quoteKey: "review3Quote", name: "Sneha M.", season: "Deep Winter" },
  { quoteKey: "review4Quote", name: "Zara K.", season: "Cool Winter" },
];

function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <span className={classes.eyebrow}>
      <span className={classes.eyebrowDots}>
        {SEASON_DOTS.map((c) => (
          <span key={c} style={{ background: c }} />
        ))}
      </span>
      {children}
    </span>
  );
}

function SpectrumRail() {
  return (
    <div className={classes.rail} role="img" aria-label="The 12 seasonal color palettes">
      {RAIL_STOPS.map((stops, i) => (
        <div
          key={i}
          className={classes.railSeg}
          style={{ background: `linear-gradient(90deg, ${stops[0]}, ${stops[1]} 55%, ${stops[2]})` }}
        />
      ))}
    </div>
  );
}

function Folio({ label, title, sub }: { label: string; title: ReactNode; sub?: string }) {
  return (
    <div className={classes.folio}>
      <span className={classes.folioLabel}>{label}</span>
      <div>
        <h2 className={classes.folioTitle}>{title}</h2>
        {sub && <p className={classes.folioSub}>{sub}</p>}
      </div>
    </div>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

export default function Content() {
  const t = useTranslations("aiColorAnalysis.landing");
  const offer = useTranslations("aiColorAnalysis.paidOffer");
  const router = useRouter();
  const isMobile = useMediaQuery("(max-width: 48em)");

  // Camera modal
  const [cameraOpen, { open: openCamera, close: closeCamera }] = useDisclosure(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const captureCanvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const cameraRequested = useRef(false);
  const openUploadRef = useRef<() => void>(null);
  const [preparingPhoto, setPreparingPhoto] = useState(false);
  const [photoError, setPhotoError] = useState("");
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState(false);

  useEffect(() => () => {
    cameraRequested.current = false;
    streamRef.current?.getTracks().forEach((track) => track.stop());
  }, []);

  // Screenshot zoom modal
  const [zoomImage, setZoomImage] = useState<string | null>(null);

  // Welcome-back card: a previously saved free guided result (Task 5).
  const [savedResult, setSavedResult] = useState<{
    photo: string;
    samples: unknown;
    season: string;
    subSeason: string;
    seasonLabel: string;
    palette: { name: string; hex: string }[];
    savedAt: number;
  } | null>(null);

  useEffect(() => {
    try {
      const saved = localStorage.getItem("caFreeResult");
      if (saved) setSavedResult(JSON.parse(saved));
    } catch {
      // Corrupt/unavailable storage — just skip the welcome-back card.
    }
  }, []);

  const handleViewSavedResult = () => {
    if (!savedResult) return;
    trackCtaClick("qa_welcome_back_view");
    sessionStorage.setItem("colorAnalysisImage", savedResult.photo);
    if (savedResult.samples) {
      sessionStorage.setItem("qaGuidedVerdict", JSON.stringify({ season: savedResult.season, subSeason: savedResult.subSeason }));
    }
    router.push("/ai-color-analysis/self-assessment");
  };

  const handleStartFresh = () => {
    trackCtaClick("qa_welcome_back_fresh");
    try {
      localStorage.removeItem("caFreeResult");
    } catch {
      // Nothing to clean up if storage isn't available.
    }
    setSavedResult(null);
  };

  // ============================================
  // IMAGE HANDLING
  // ============================================

  const resizeImage = (file: File, maxWidth: number, maxHeight: number): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new window.Image();
      const url = URL.createObjectURL(file);
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Photo unavailable")); };
      img.onload = () => {
        try {
          let { width, height } = img;
          if (!width || !height) throw new Error("Photo unavailable");
          const scale = Math.min(maxWidth / width, maxHeight / height, 1);
          width = Math.round(width * scale);
          height = Math.round(height * scale);

          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          if (!ctx) throw new Error("Photo unavailable");
          ctx.drawImage(img, 0, 0, width, height);

          const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
          resolve(dataUrl);
        } catch (error) {
          reject(error);
        } finally {
          URL.revokeObjectURL(url);
        }
      };
      img.src = url;
    });
  };

  const handlePhotoDrop = async (files: File[]) => {
    if (!files.length || preparingPhoto) return;
    setPreparingPhoto(true);
    setPhotoError("");
    try {
      const dataUrl = await resizeImage(files[0], 1200, 1200);
      sessionStorage.setItem("colorAnalysisImage", dataUrl);
      trackPhotoUpload("AI Color Analysis");
      router.push(QUICK_ANALYSIS_V1 ? "/ai-color-analysis/choose-method" : "/ai-color-analysis/result");
    } catch {
      setPhotoError(t("photoError"));
      setPreparingPhoto(false);
    }
  };

  // Camera functions
  const startCamera = async () => {
    try {
      if (!cameraRequested.current) return;
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: false,
      });
      if (cameraRequested.current && videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
      } else {
        stream.getTracks().forEach((track) => track.stop());
      }
    } catch {
      if (cameraRequested.current) setCameraError(true);
    }
  };

  const stopCamera = () => {
    cameraRequested.current = false;
    setCameraReady(false);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  const takePhoto = async () => {
    if (cameraReady && videoRef.current && captureCanvasRef.current) {
      try {
        const video = videoRef.current;
        const canvas = captureCanvasRef.current;

        let width = video.videoWidth;
        let height = video.videoHeight;
        if (!width || !height) throw new Error("Camera not ready");
        const scale = Math.min(1200 / width, 1200 / height, 1);
        width = Math.round(width * scale);
        height = Math.round(height * scale);

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) throw new Error("Camera unavailable");
        {
          ctx.drawImage(video, 0, 0, width, height);
          const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
          sessionStorage.setItem("colorAnalysisImage", dataUrl);
          trackPhotoUpload("AI Color Analysis");
          closeCamera();
          stopCamera();
          router.push(QUICK_ANALYSIS_V1 ? "/ai-color-analysis/choose-method" : "/ai-color-analysis/result");
        }
      } catch {
        setCameraError(true);
      }
    }
  };

  const handleOpenCamera = () => {
    cameraRequested.current = true;
    setCameraError(false);
    setCameraReady(false);
    openCamera();
    setTimeout(() => startCamera(), 100);
  };

  const handleCloseCamera = () => {
    stopCamera();
    closeCamera();
  };

  // Shared card renderer for the product-screenshot sections
  const shotCard = (src: string, alt: string, extra?: React.CSSProperties) => (
    <Box component="button" type="button" key={src} onClick={() => setZoomImage(src)} className={classes.shotCard} aria-label={`${t("tapToView")}: ${alt}`} style={extra}>
      <Box className={classes.shotCardInner}>
        <img src={src} alt={alt} style={{ width: "100%", height: "auto", display: "block" }} loading="lazy" />
      </Box>
    </Box>
  );

  const mobileScrollStyle: React.CSSProperties = {
    display: "flex",
    gap: 12,
    overflowX: "auto",
    paddingBottom: 8,
    WebkitOverflowScrolling: "touch",
    scrollSnapType: "x mandatory",
  };

  // ============================================
  // RENDER
  // ============================================

  return (
    <Container size="xl" pt={{ base: "1rem", md: "2.5rem" }} pb={{ base: "3rem", md: "5rem" }} pos="relative">
      {/* ============ Hero — promise left, the scan card right ============ */}
      <Box className={classes.hero}>
        <Box className={classes.heroIntro}>
          {/* Weekly rate, not a lifetime total — must stay at or below what
              Gizmo actually measures (quiz completes + uploads) */}
          <Eyebrow>{t("weeklyProof")}</Eyebrow>
          {/* State what is free before visitors choose or upload a photo. */}
          <h1 className={classes.heroTitle}>
            {t.rich("heading", {
              accent: (chunks) => <span className={classes.heroAccent}>{chunks}</span>,
            })}
          </h1>
          <p className={classes.heroSubtitle}>{t("subheading2")}</p>
        </Box>

        {/* The scan card — the page's one conversion action */}
        <Box className={classes.scanCard}>
          {savedResult && (
            <Paper withBorder radius="md" p="sm" mb="sm" style={{ backgroundColor: "#fdf5f8", borderColor: "#f3d4e0" }}>
              <Stack gap={8}>
                <Text fz="xs" fw={600} tt="uppercase" style={{ color: "#8b8b98", letterSpacing: 0.5 }}>
                  Welcome back
                </Text>
                <Text fz="sm" fw={600} style={{ color: "#16181d" }}>
                  {savedResult.seasonLabel}
                </Text>
                {savedResult.palette?.length > 0 && (
                  <Group gap={0} style={{ borderRadius: 6, overflow: "hidden" }}>
                    {savedResult.palette.slice(0, 8).map((color, i) => (
                      <Box key={i} style={{ flex: 1, height: 20, background: color.hex }} />
                    ))}
                  </Group>
                )}
                <Button size="xs" radius="xl" onClick={handleViewSavedResult}>
                  View my result
                </Button>
                <Text
                  fz={11}
                  ta="center"
                  onClick={handleStartFresh}
                  style={{ color: "#8b8b98", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 2 }}
                >
                  Start fresh
                </Text>
              </Stack>
            </Paper>
          )}

          <h2 className={classes.scanCardTitle}>{t("scanTitle")}</h2>

          <Dropzone
            role="button"
            aria-label={t("uploadButton")}
            onDrop={handlePhotoDrop}
            onReject={(files) => setPhotoError(t(files.some(file => file.errors.some(error => error.code === "file-too-large")) ? "photoTooLarge" : "photoTypeError"))}
            openRef={openUploadRef}
            useFsAccessApi={false}
            loading={preparingPhoto}
            disabled={preparingPhoto}
            maxSize={10 * 1024 ** 2}
            accept={["image/jpeg", "image/png", "image/webp"]}
            multiple={false}
            h={170}
            radius="lg"
            styles={{
              root: {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#ffffff",
                border: "2px dashed #cbd5e1",
                "&:hover": {
                  borderColor: "#b3265c",
                  backgroundColor: "#fdf5f8",
                },
              },
            }}
          >
            <Flex direction="column" align="center" justify="center" style={{ pointerEvents: "none" }}>
              <Dropzone.Accept>
                <IconDownload style={{ width: rem(36), height: rem(36), color: "#22c55e" }} stroke={1.5} />
              </Dropzone.Accept>
              <Dropzone.Reject>
                <IconX style={{ width: rem(36), height: rem(36), color: "#ef4444" }} stroke={1.5} />
              </Dropzone.Reject>
              <Dropzone.Idle>
                <IconCloudUpload style={{ width: rem(36), height: rem(36), color: "#8b8b98" }} stroke={1.5} />
              </Dropzone.Idle>
              <span className={classes.uploadAction}>{preparingPhoto ? t("photoLoading") : t("uploadButton")}</span>
              <Text size="xs" ta="center" mt={4} style={{ color: "#8b8b98" }}>
                {t("hint")}
              </Text>
              <Text size="xs" ta="center" mt={4} c="dimmed">JPG · PNG · WebP · 10 MB</Text>
            </Flex>
          </Dropzone>
          {photoError && <Alert color="red" role="alert" mt="sm">{photoError}</Alert>}
          <Text role="status" fz="xs" ta="center" mt="sm" c="dimmed">{preparingPhoto ? t("photoLoading") : t("trustBadges")}</Text>

          {!isMobile && (
            <>
              <Button
                size="sm"
                radius="xl"
                variant="subtle"
                color="dark"
                mt="xs"
                disabled={preparingPhoto}
                leftSection={<IconCamera size={18} />}
                onClick={handleOpenCamera}
                fullWidth
              >
                {t("cameraButton")}
              </Button>
            </>
          )}

          <Text fz="xs" ta="center" mt="sm" style={{ color: "#8b8b98" }}>
            {t("uploadOnceChoose")}
          </Text>

          {/* What happens next: selfie → drapes, in miniature */}
          <Box className={classes.scanPreviewStrip}>
            <img src="/mainLanding/hero/selfie.webp" alt="" aria-hidden="true" />
            <span className={classes.scanPreviewArrow}>
              <IconArrowRight size={16} />
            </span>
            <img src="/mainLanding/hero/drape-azure.webp" alt="" aria-hidden="true" />
            <img src="/mainLanding/hero/drape-burgundy.webp" alt="" aria-hidden="true" />
            <img src="/mainLanding/hero/drape-magenta.webp" alt="" aria-hidden="true" />
          </Box>
          <p className={classes.scanPreviewCaption}>{offer("sample")}</p>
        </Box>
        <Box className={classes.heroSample}>
          <SampleReportButton source="analysis_landing" preview onContinue={() => openUploadRef.current?.()} continueLabel={t("uploadButton")} />
        </Box>
      </Box>

      <SpectrumRail />

      {/* ============ Social proof — real reviews, verbatim ============ */}
      <Box mt={{ base: 40, md: 56 }}>
        <Box ta="center" mb={24}>
          <span className={classes.reviewStars} aria-hidden="true">
            ★★★★★
          </span>
          <div className={classes.counterLabel}>{t("reviewsKicker")}</div>
        </Box>
        {/* Mobile: horizontal scroll */}
        <Box hiddenFrom="sm" style={mobileScrollStyle}>
          {REVIEWS.map((r) => (
            <Box key={r.name} style={{ flexShrink: 0, width: 260, scrollSnapAlign: "start" }}>
              <div className={classes.reviewCard}>
                <span className={classes.reviewStars} aria-hidden="true">
                  ★★★★★
                </span>
                <p className={classes.reviewQuote}>&ldquo;{t(r.quoteKey)}&rdquo;</p>
                <p className={classes.reviewMeta}>
                  {r.name} · <span>{r.season}</span>
                </p>
              </div>
            </Box>
          ))}
        </Box>
        {/* Desktop: grid */}
        <SimpleGrid cols={{ sm: 2, lg: 4 }} spacing="md" visibleFrom="sm">
          {REVIEWS.map((r) => (
            <div key={r.name} className={classes.reviewCard}>
              <span className={classes.reviewStars} aria-hidden="true">
                ★★★★★
              </span>
              <p className={classes.reviewQuote}>&ldquo;{t(r.quoteKey)}&rdquo;</p>
              <p className={classes.reviewMeta}>
                {r.name} · <span>{r.season}</span>
              </p>
            </div>
          ))}
        </SimpleGrid>
        <Text fz="xs" ta="center" mt="md" style={{ color: "#8b8b98" }}>
          {t("reviewsNote")}
        </Text>
      </Box>

      {/* ============ Real season results ============ */}
      <Folio
        label={t("folioProof")}
        title={t.rich("sectionRealResults", {
          accent: (chunks) => <span className={classes.heroAccent}>{chunks}</span>,
        })}
      />
      {/* Mobile: horizontal scroll */}
      <Box hiddenFrom="sm" mt={28} style={mobileScrollStyle}>
        {["/swatchPreviews/season1.png", "/swatchPreviews/season2.png", "/swatchPreviews/season3.png"].map((src, i) => (
          <Box key={src} style={{ flexShrink: 0, width: 260, scrollSnapAlign: "start" }}>
            {shotCard(src, `Season result ${i + 1}`)}
          </Box>
        ))}
      </Box>
      {/* Desktop: grid */}
      <SimpleGrid cols={3} spacing="lg" visibleFrom="sm" mt={32}>
        {["/swatchPreviews/season1.png", "/swatchPreviews/season2.png", "/swatchPreviews/season3.png"].map((src, i) =>
          shotCard(src, `Season result ${i + 1}`)
        )}
      </SimpleGrid>
      <Text fz="xs" ta="center" mt="sm" style={{ color: "#8b8b98" }}>
        {t("tapToView")}
      </Text>

      {/* ============ Personalized colors ============ */}
      <Folio
        label={t("folioPalette")}
        title={t.rich("sectionColors", {
          accent: (chunks) => <span className={classes.heroAccent}>{chunks}</span>,
        })}
      />
      <Box hiddenFrom="sm" mt={28} style={mobileScrollStyle}>
        {["/swatchPreviews/palette1.png", "/swatchPreviews/palette2.png"].map((src, i) => (
          <Box key={src} style={{ flexShrink: 0, width: "85%", scrollSnapAlign: "center" }}>
            {shotCard(src, `Color palette ${i + 1}`)}
          </Box>
        ))}
      </Box>
      <SimpleGrid cols={2} spacing="lg" visibleFrom="sm" mt={32}>
        {["/swatchPreviews/palette1.png", "/swatchPreviews/palette2.png"].map((src, i) =>
          shotCard(src, `Color palette ${i + 1}`)
        )}
      </SimpleGrid>

      {/* ============ Outfit previews ============ */}
      <Folio
        label={t("folioOutfits")}
        title={t.rich("sectionOutfits", {
          accent: (chunks) => <span className={classes.heroAccent}>{chunks}</span>,
        })}
      />
      {/* Mobile: horizontal scroll */}
      <Box hiddenFrom="md" mt={28} style={{ ...mobileScrollStyle, alignItems: "center" }}>
        {[
          { src: "/swatchPreviews/girlCasual1.png", label: t("casual") },
          { src: "/swatchPreviews/girlProfessional3.png", label: t("professional") },
          { src: "/swatchPreviews/girlFormal1.png", label: t("formal") },
          { src: "/swatchPreviews/girlFormal2.png", label: t("formal") },
        ].map((item) => (
          <Box key={item.src} style={{ flexShrink: 0, width: "75%", scrollSnapAlign: "center" }}>
            <p className={classes.shotLabel}>{item.label}</p>
            {shotCard(item.src, item.label)}
          </Box>
        ))}
      </Box>
      {/* Desktop: 3 columns — formal stacked vertically */}
      <SimpleGrid cols={3} spacing="lg" visibleFrom="md" mt={32}>
        <Box>
          <p className={classes.shotLabel}>{t("casual")}</p>
          {shotCard("/swatchPreviews/girlCasual1.png", "Casual & Everyday")}
        </Box>
        <Box>
          <p className={classes.shotLabel}>{t("professional")}</p>
          {shotCard("/swatchPreviews/girlProfessional3.png", "Professional")}
        </Box>
        <Box>
          <p className={classes.shotLabel}>{t("formal")}</p>
          <Stack gap="sm">
            {shotCard("/swatchPreviews/girlFormal1.png", "Formal & Evening")}
            {shotCard("/swatchPreviews/girlFormal2.png", "Formal & Evening")}
          </Stack>
        </Box>
      </SimpleGrid>

      {/* ============ Makeup ============ */}
      <Folio
        label={t("folioMakeup")}
        title={t.rich("sectionMakeup", {
          accent: (chunks) => <span className={classes.heroAccent}>{chunks}</span>,
        })}
      />
      <Box hiddenFrom="md" mt={28} style={mobileScrollStyle}>
        {[
          { src: "/swatchPreviews/eyeshadow1.png", alt: "Eyeshadow palette recommendations" },
          { src: "/swatchPreviews/blush1.png", alt: "Blush and lip color recommendations" },
        ].map((item) => (
          <Box key={item.src} style={{ flexShrink: 0, width: "85%", scrollSnapAlign: "center" }}>
            {shotCard(item.src, item.alt)}
          </Box>
        ))}
      </Box>
      <SimpleGrid cols={2} spacing="lg" visibleFrom="md" mt={32}>
        {shotCard("/swatchPreviews/eyeshadow1.png", "Eyeshadow palette recommendations")}
        {shotCard("/swatchPreviews/blush1.png", "Blush and lip color recommendations")}
      </SimpleGrid>

      {/* ============ FAQ ============ */}
      <Folio
        label={t("folioFaq")}
        title={t.rich("sectionFaq", {
          accent: (chunks) => <span className={classes.heroAccent}>{chunks}</span>,
        })}
      />
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md" mt={32}>
        {[1, 2, 3, 4, 5, 6, 7].map((num) => (
          <Box key={num} className={classes.faqCard}>
            <h3 className={classes.faqQuestion}>{t(`faq${num}Question`)}</h3>
            <p className={classes.faqAnswer}>
              {num === 4
                ? t("faq4Answer").replace("${price}", PRICING.AI_COLOR_ANALYSIS.BASE_PRICE.toString())
                : t(`faq${num}Answer`)}
            </p>
          </Box>
        ))}
      </SimpleGrid>

      {/* Camera Modal */}
      <Modal opened={cameraOpen} onClose={handleCloseCamera} title={t("cameraModalTitle")} centered size="md">
        <Stack>
          <Box h={350} style={{ overflow: "hidden", borderRadius: 12 }}>
            <video ref={videoRef} autoPlay playsInline onLoadedData={() => setCameraReady(true)} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          </Box>
          {cameraError ? <Alert color="red" role="alert">{t("cameraError")}</Alert> : !cameraReady && <Text role="status" size="sm">{t("cameraLoading")}</Text>}
          <Button
            onClick={takePhoto}
            disabled={!cameraReady || cameraError}
            leftSection={<IconCamera size={18} />}
            size="lg"
            radius="md"
            style={{ background: "#16181d", color: "white" }}
          >
            {t("captureButton")}
          </Button>
          <canvas ref={captureCanvasRef} style={{ display: "none" }} />
        </Stack>
      </Modal>

      {/* Screenshot Zoom Modal */}
      <Modal
        opened={!!zoomImage}
        onClose={() => setZoomImage(null)}
        size="xl"
        centered
        padding={0}
        title={t("tapToView")}
        closeButtonProps={{ "aria-label": "Close preview" }}
        styles={{
          body: { padding: 0 },
          content: { background: "transparent", boxShadow: "none" },
        }}
        overlayProps={{ backgroundOpacity: 0.7 }}
      >
        <Box
          onClick={() => setZoomImage(null)}
          style={{
            cursor: "pointer",
            maxHeight: "90vh",
            overflow: "auto",
            borderRadius: 16,
            background: "white",
          }}
        >
          {zoomImage && (
            <img
              src={zoomImage}
              alt="Full preview"
              style={{ width: "100%", height: "auto", display: "block", borderRadius: 16 }}
            />
          )}
        </Box>
      </Modal>
    </Container>
  );
}
